import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ['energy', 'health', 'ngo', 'mining', 'agriculture', 'infrastructure', 'services', 'other'];
const SECTOR_LABELS = {
    energy: 'Énergie', health: 'Santé', ngo: 'ONG', mining: 'Mines',
    agriculture: 'Agriculture', infrastructure: 'Infrastructures',
    services: 'Services', other: 'Autre',
};
const TEAM_SIZES = ['1-10', '10-50', '50-200', '200+'];
const CURRENCIES = ['EUR', 'XOF', 'XAF', 'USD'];
const ALLOWED_SOURCES = new Set(['campus-public', 'diagnostic-quiz', 'landing', 'donate', 'cms', 'footer', 'demo', 'project-score']);

const NEED_OPTIONS = [
    'Structurer et piloter mes projets',
    'Reporting bailleurs & conformité',
    'Suivi budgétaire multi-devises',
    'Gouvernance multi-entités / consolidation',
    'Évaluer la plateforme pour mon organisation',
    'Autre besoin',
];
const PLAN_INTEREST_OPTIONS = ['Explorer (gratuit)', 'Essentiel', 'Impact', 'Organisation', 'Institution (sur devis)', 'Je ne sais pas encore'];

export default function AccessRequest() {
    const [params] = useSearchParams();
    const incomingSource = params.get('source');
    const cleanSource = incomingSource && ALLOWED_SOURCES.has(incomingSource) ? incomingSource : null;
    const isDemo = cleanSource === 'demo';

    useEffect(() => {
        if (isDemo) trackEvent('demo_form_view');
    }, [isDemo]);

    // Step 1 (required) : 4 champs critiques
    // Step 2 (optionnel) : enrichissement profil après lead capturé
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '', organization: '',
        // Step 2 (optionnels)
        legal_entity: '', country: '', phone: '',
        sector: '', team_size: '',
        funds_to_manage_amount: '', funds_to_manage_currency: 'EUR',
        how_did_you_hear: '', message: '',
        primary_need: '', plan_interest: '',
        consent_cgu: false,
    });
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(false);
    const [leadId, setLeadId] = useState(null);

    const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const canSubmitStep1 = form.first_name.trim() && form.last_name.trim()
        && form.email.includes('@') && form.organization.trim() && form.consent_cgu
        && (!isDemo || form.primary_need);

    const submitStep1 = async (e) => {
        e?.preventDefault?.();
        if (!canSubmitStep1) {
            toast.error('Veuillez remplir tous les champs marqués * et accepter les conditions');
            return;
        }
        setBusy(true);
        try {
            const payload = {
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                email: form.email.trim(),
                organization: form.organization.trim(),
            };
            if (cleanSource) payload.source = cleanSource;
            if (form.primary_need || form.plan_interest) {
                payload.message = [
                    form.primary_need ? `Besoin principal : ${form.primary_need}` : null,
                    form.plan_interest ? `Offre envisagée : ${form.plan_interest}` : null,
                ].filter(Boolean).join('\n');
            }
            const r = await axios.post(`${API}/access-requests/create`, payload);
            setLeadId(r.data.id);
            trackEvent(isDemo ? 'demo_requested' : 'apply_form_submitted', {
                source: cleanSource || 'direct',
                ...(form.primary_need ? { need: form.primary_need } : {}),
            });
            setStep(2);
        } catch (e2) {
            toast.error(e2.response?.data?.detail || e2.message || 'Envoi échoué');
        } finally {
            setBusy(false);
        }
    };

    const submitStep2 = async (e) => {
        e?.preventDefault?.();
        if (!leadId) {
            setSuccess(true);
            return;
        }
        setBusy(true);
        try {
            // Enrichit la lead créée à l'étape 1 via PATCH /access-requests/{id}
            const enrichment = {
                country: form.country.trim() || null,
                phone: form.phone.trim() || null,
                legal_entity: form.legal_entity.trim() || null,
                sector: form.sector || null,
                team_size: form.team_size || null,
                how_did_you_hear: form.how_did_you_hear.trim() || null,
                message: form.message.trim() || null,
            };
            if (form.funds_to_manage_amount !== '' && form.funds_to_manage_amount != null) {
                const amt = Number(form.funds_to_manage_amount);
                if (!Number.isNaN(amt) && amt >= 0) {
                    enrichment.funds_to_manage_amount = amt;
                    enrichment.funds_to_manage_currency = form.funds_to_manage_currency;
                }
            }
            // Nettoie : on n'envoie que les champs renseignés
            const cleaned = Object.fromEntries(Object.entries(enrichment).filter(([_, v]) => v !== null && v !== ''));
            if (Object.keys(cleaned).length > 0) {
                await axios.patch(`${API}/access-requests/${leadId}/enrich`, cleaned);
            }
            setSuccess(true);
        } catch (e2) {
            // Non bloquant — la lead est déjà créée à l'étape 1
            toast.error(e2.response?.data?.detail || 'Enrichissement partiellement échoué, mais votre demande est enregistrée');
            setSuccess(true);
        } finally {
            setBusy(false);
        }
    };

    const skipStep2 = () => setSuccess(true);

    // ─── SUCCESS SCREEN ──────────────────────────────────────────
    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-6">
                <div className="max-w-lg w-full card-soft p-10 text-center" data-testid="access-request-success">
                    <div className="h-16 w-16 rounded-full bg-primary-light text-primary mx-auto flex items-center justify-center mb-5">
                        <CheckCircle2 size={32} />
                    </div>
                    <h1 className="font-heading text-3xl mb-3">Demande envoyée</h1>
                    <p className="text-foreground/70 mb-3 leading-relaxed">
                        Merci <strong>{form.first_name}</strong> ! Notre équipe African Valley reviendra vers vous sous
                        <strong> 48 heures ouvrées</strong>.
                    </p>
                    <p className="text-sm text-foreground/55 mb-6">
                        Un email de confirmation a été envoyé à <strong>{form.email}</strong>.
                    </p>
                    <div className="flex flex-col gap-2.5">
                        <Link to="/p/contact"
                           className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-primary hover:bg-primary-hover text-primary-foreground text-sm font-medium" data-testid="success-cta-contact-rdv">
                            📅 Réserver un rendez-vous — nous écrire
                        </Link>
                        <Link to="/p/campus" className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-input bg-background hover:bg-surface-muted text-sm font-medium" data-testid="success-cta-campus">
                            <Sparkles size={14} /> Découvrir Campus 360°
                        </Link>
                        <Link to="/p/diagnostic" className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-input bg-background hover:bg-surface-muted text-sm font-medium">
                            Faire le diagnostic gratuit (3 min)
                        </Link>
                        <Link to="/" className="text-xs text-foreground/55 hover:text-foreground mt-2" data-testid="access-back-home">
                            ← Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // ─── WIZARD HEADER + PROGRESS ────────────────────────────────
    const totalSteps = 2;
    const progressPct = step === 1 ? 50 : 100;

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src="/brand/african-valley-logo-96.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover ring-1 ring-border shadow-soft" />
                        <span className="font-heading text-2xl">
                            <span className="text-foreground">African</span> <span className="text-primary">Valley</span>
                        </span>
                    </Link>
                    <Link to="/auth/signin" className="text-sm text-foreground/70 hover:text-foreground">Déjà client ? Connexion</Link>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-6 py-12 lg:py-16">
                {/* Progress bar */}
                <div className="mb-10">
                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-foreground/55 mb-3">
                        <span data-testid="apply-step-label">Étape {step} / {totalSteps}</span>
                        <span>{progressPct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-primary to-amber-400 transition-all duration-500 ease-out"
                             style={{ width: `${progressPct}%` }} data-testid="apply-progress-bar" />
                    </div>
                </div>

                {cleanSource && (
                    <div className="mb-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary-light text-primary text-xs font-medium border border-primary/15"
                         data-testid={`access-source-${cleanSource}`}>
                        <CheckCircle2 size={13} />
                        {cleanSource === 'campus-public' && 'Provenance : Campus 360°'}
                        {cleanSource === 'diagnostic-quiz' && 'Provenance : Diagnostic gratuit'}
                        {cleanSource === 'landing' && 'Provenance : Landing'}
                        {cleanSource === 'donate' && 'Provenance : Page Donation'}
                        {cleanSource === 'cms' && 'Provenance : Article CMS'}
                        {cleanSource === 'footer' && 'Provenance : Footer'}
                        {cleanSource === 'demo' && 'Demande de démonstration personnalisée'}
                        {cleanSource === 'project-score' && 'Provenance : Project Score™'}
                    </div>
                )}

                {/* ─── STEP 1 : 4 champs critiques ─── */}
                {step === 1 && (
                    <>
                        <div className="eyebrow mb-2">{isDemo ? 'Demande de démonstration — 60 secondes' : "Demande d'accès — 30 secondes"}</div>
                        <h1 className="font-heading text-4xl lg:text-5xl mb-3" data-testid="apply-step1-title">
                            {isDemo ? <>Voyons la plateforme <span className="text-primary">en action</span>.</> : <>Démarrons par <span className="text-primary">l'essentiel</span>.</>}
                        </h1>
                        <p className="text-foreground/70 mb-8 leading-relaxed">
                            {isDemo
                                ? 'Dites-nous qui vous êtes et ce que vous cherchez : un expert vous montre la plateforme sur VOS cas d\'usage, sous 48 h ouvrées.'
                                : '4 informations suffisent pour ouvrir le dialogue. Vous pourrez compléter votre profil ensuite.'}
                        </p>

                        <form onSubmit={submitStep1} className="card-soft p-7 space-y-5" data-testid="access-request-form-step1">
                            {/* Honeypot anti-bot */}
                            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
                                <label>Site web (ne pas remplir)
                                    <input type="text" tabIndex={-1} autoComplete="off"
                                        onChange={(e) => updateForm({ website: e.target.value })} />
                                </label>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Prénom *">
                                    <Input required value={form.first_name}
                                        onChange={(e) => updateForm({ first_name: e.target.value })}
                                        data-testid="ar-first-name" autoFocus />
                                </Field>
                                <Field label="Nom *">
                                    <Input required value={form.last_name}
                                        onChange={(e) => updateForm({ last_name: e.target.value })}
                                        data-testid="ar-last-name" />
                                </Field>
                            </div>
                            <Field label="Email professionnel *">
                                <Input required type="email" value={form.email}
                                    onChange={(e) => updateForm({ email: e.target.value })}
                                    placeholder="vous@organisation.com"
                                    data-testid="ar-email" />
                            </Field>
                            <Field label="Organisation *" hint="Nom usuel de votre structure">
                                <Input required value={form.organization}
                                    onChange={(e) => updateForm({ organization: e.target.value })}
                                    placeholder="ex. Ministère X, ONG Y, Société Z"
                                    data-testid="ar-organization" />
                            </Field>

                            {isDemo && (
                                <>
                                    <Field label="Votre besoin principal *">
                                        <select
                                            required
                                            value={form.primary_need}
                                            onChange={(e) => updateForm({ primary_need: e.target.value })}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                            data-testid="ar-primary-need">
                                            <option value="">— Sélectionnez —</option>
                                            {NEED_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Offre envisagée" hint="Optionnel — nous adapterons la démo">
                                        <select
                                            value={form.plan_interest}
                                            onChange={(e) => updateForm({ plan_interest: e.target.value })}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                            data-testid="ar-plan-interest">
                                            <option value="">— Je ne sais pas encore —</option>
                                            {PLAN_INTEREST_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </Field>
                                </>
                            )}

                            {/* Consent CGU */}
                            <label className="flex items-start gap-3 pt-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={form.consent_cgu}
                                    onChange={(e) => updateForm({ consent_cgu: e.target.checked })}
                                    className="mt-1 h-4 w-4 rounded border-input text-primary focus:ring-primary"
                                    data-testid="ar-consent"
                                />
                                <span className="text-xs text-foreground/65 leading-relaxed">
                                    J'accepte d'être recontacté par l'équipe African Valley et j'ai pris connaissance
                                    {' '}des <Link to="/p/cgu" target="_blank" className="text-primary hover:underline">CGU</Link>
                                    {' '}et de la <Link to="/p/privacy" target="_blank" className="text-primary hover:underline">politique de confidentialité</Link>. *
                                </span>
                            </label>

                            <Button type="submit" disabled={!canSubmitStep1 || busy}
                                className="w-full bg-primary hover:bg-primary-hover h-12 text-base"
                                data-testid="ar-submit-step1">
                                {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                                Continuer <ArrowRight size={16} className="ml-2" />
                            </Button>
                            <p className="text-[11px] text-muted-foreground text-center">
                                Vos données restent confidentielles · Désabonnement en 1 clic
                            </p>
                        </form>
                    </>
                )}

                {/* ─── STEP 2 : enrichissement optionnel ─── */}
                {step === 2 && (
                    <>
                        <div className="eyebrow mb-2 text-secondary">Étape 2 sur 2 — optionnelle</div>
                        <h1 className="font-heading text-4xl lg:text-5xl mb-3" data-testid="apply-step2-title">
                            Aidez-nous à <span className="text-primary">mieux vous servir</span>.
                        </h1>
                        <p className="text-foreground/70 mb-8 leading-relaxed">
                            Votre demande est <strong className="text-foreground">déjà enregistrée</strong> ✓.
                            Ces informations nous permettent de préparer un onboarding sur mesure. Vous pouvez les passer.
                        </p>

                        <form onSubmit={submitStep2} className="card-soft p-7 space-y-6" data-testid="access-request-form-step2">
                            <Section title="Votre organisation">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Field label="Téléphone">
                                        <Input value={form.phone}
                                            onChange={(e) => updateForm({ phone: e.target.value })}
                                            placeholder="+221 77 000 00 00"
                                            data-testid="ar-phone" />
                                    </Field>
                                    <Field label="Pays">
                                        <Input value={form.country}
                                            onChange={(e) => updateForm({ country: e.target.value })}
                                            placeholder="Sénégal, Côte d'Ivoire…"
                                            data-testid="ar-country" />
                                    </Field>
                                    <Field label="Raison sociale" hint="Dénomination légale">
                                        <Input value={form.legal_entity}
                                            onChange={(e) => updateForm({ legal_entity: e.target.value })}
                                            data-testid="ar-legal-entity" />
                                    </Field>
                                    <Field label="Taille de l'équipe">
                                        <select value={form.team_size}
                                            onChange={(e) => updateForm({ team_size: e.target.value })}
                                            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                                            data-testid="ar-team-size">
                                            <option value="">— Choisir —</option>
                                            {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Secteur d'activité">
                                    <div className="flex flex-wrap gap-2">
                                        {SECTORS.map(s => (
                                            <button type="button" key={s}
                                                onClick={() => updateForm({ sector: form.sector === s ? '' : s })}
                                                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${form.sector === s ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-surface-muted'}`}
                                                data-testid={`ar-sector-${s}`}>
                                                {SECTOR_LABELS[s]}
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                            </Section>

                            <Section title="Votre projet">
                                <Field label="Fonds à gérer" hint="Budget approximatif du portefeuille de projets">
                                    <div className="grid grid-cols-3 gap-2">
                                        <Input type="number" min="0" step="1000" className="col-span-2"
                                            placeholder="ex. 500000"
                                            value={form.funds_to_manage_amount}
                                            onChange={(e) => updateForm({ funds_to_manage_amount: e.target.value })}
                                            data-testid="ar-funds-amount" />
                                        <select value={form.funds_to_manage_currency}
                                            onChange={(e) => updateForm({ funds_to_manage_currency: e.target.value })}
                                            className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                                            data-testid="ar-funds-currency">
                                            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                </Field>
                                <Field label="Comment nous avez-vous connus ?">
                                    <Input value={form.how_did_you_hear}
                                        onChange={(e) => updateForm({ how_did_you_hear: e.target.value })}
                                        placeholder="Recommandation, presse, LinkedIn, événement…"
                                        data-testid="ar-how-heard" />
                                </Field>
                                <Field label="Vos attentes" hint="Combien de projets ? Modules d'intérêt ? Bailleurs ?">
                                    <Textarea rows={4} value={form.message}
                                        onChange={(e) => updateForm({ message: e.target.value })}
                                        placeholder="Décrivez en quelques lignes votre besoin…"
                                        data-testid="ar-message" />
                                </Field>
                            </Section>

                            <div className="pt-2 border-t border-border flex flex-col sm:flex-row gap-3">
                                <Button type="button" variant="outline" onClick={skipStep2} disabled={busy}
                                    className="sm:order-1 h-12 sm:flex-1" data-testid="ar-skip-step2">
                                    <ArrowLeft size={14} className="mr-2" /> Passer cette étape
                                </Button>
                                <Button type="submit" disabled={busy}
                                    className="sm:order-2 bg-primary hover:bg-primary-hover h-12 sm:flex-[2] text-base"
                                    data-testid="ar-submit-step2">
                                    {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                                    Envoyer toutes les informations
                                </Button>
                            </div>
                        </form>
                    </>
                )}
            </main>
        </div>
    );
}

const Field = ({ label, hint, children }) => (
    <div className="space-y-1.5">
        <div className="text-sm font-medium">{label}</div>
        {children}
        {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
);

const Section = ({ title, children }) => (
    <div className="space-y-4">
        <div className="eyebrow text-secondary">{title}</div>
        <div className="space-y-4">{children}</div>
    </div>
);
